
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { requestPasswordReset } from '../services/authService';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { PasswordInput } from './PasswordInput';

interface AuthModalProps {
  modalView: 'none' | 'login' | 'signup' | 'forgotPassword' | 'resetEmailSent';
  setModalView: (view: 'none' | 'login' | 'signup' | 'forgotPassword' | 'resetEmailSent') => void;
  closeModal: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ modalView, setModalView, closeModal }) => {
  const { login, signup } = useAuth();

  // Input refs for focus management
  const nameInputRef = useRef<HTMLInputElement>(null);
  const loginEmailInputRef = useRef<HTMLInputElement>(null);
  const forgotEmailInputRef = useRef<HTMLInputElement>(null);

  // State for login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  // State for signup form
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [isSignupPasswordStrong, setIsSignupPasswordStrong] = useState(false);
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSignupLoading, setIsSignupLoading] = useState(false);

  // State for forgot password
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (modalView !== 'none') {
      document.addEventListener('keydown', handleKeyDown);

      // Focus management
      if (modalView === 'login') {
        setTimeout(() => loginEmailInputRef.current?.focus(), 100);
      } else if (modalView === 'signup') {
        setTimeout(() => nameInputRef.current?.focus(), 100);
      } else if (modalView === 'forgotPassword') {
        setTimeout(() => forgotEmailInputRef.current?.focus(), 100);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalView, closeModal]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoginLoading(true);
    try {
      await login(loginEmail, loginPassword);
      sessionStorage.removeItem('generationCount');
      closeModal();
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupPassword !== signupConfirmPassword) {
      setSignupError('Passwords do not match.');
      return;
    }
    if (!isSignupPasswordStrong) {
      setSignupError('Password is not strong enough.');
      return;
    }
    setSignupError(null);
    setIsSignupLoading(true);
    try {
      await signup(signupName, signupEmail, signupPassword);
      sessionStorage.removeItem('generationCount');
      closeModal();
    } catch (err) {
      setSignupError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordError(null);
    setIsForgotPasswordLoading(true);

    try {
      await requestPasswordReset(forgotPasswordEmail);
      setModalView('resetEmailSent'); // success view
    } catch (err: any) {
      setForgotPasswordError(err.message || 'An unknown error occurred.');
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  if (modalView === 'none') {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal} role="dialog" aria-modal="true">
      <div className="relative bg-white shadow-lg rounded-xl p-8 w-full max-w-md border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        
        {modalView === 'login' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-[#4F46E5]">Welcome Back</h1>
              <p className="text-[#6B7280] mt-2">Log in to your account.</p>
            </div>
            <form onSubmit={handleLoginSubmit} className="space-y-6">
              {loginError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{loginError}</div>}
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-[#6B7280] mb-1">Email Address</label>
                <input ref={loginEmailInputRef} type="email" id="login-email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow" />
              </div>
              <PasswordInput
                label="Password"
                id="login-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
              <div className="text-right text-sm">
                <button type="button" onClick={() => setModalView('forgotPassword')} className="font-medium text-[#4F46E5] hover:text-[#6366F1]">
                  Forgot Password?
                </button>
              </div>
              <button type="submit" disabled={isLoginLoading} className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300 disabled:opacity-50">{isLoginLoading ? <><SpinnerIcon /> Logging In...</> : 'Log In'}</button>
            </form>
            <p className="text-center text-sm text-[#6B7280] mt-6">Don't have an account? <button onClick={() => setModalView('signup')} className="font-medium text-[#4F46E5] hover:text-[#6366F1]">Sign up</button></p>
          </div>
        )}

        {modalView === 'forgotPassword' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-[#4F46E5]">Reset Password</h1>
              <p className="text-[#6B7280] mt-2">Enter your email to receive a reset link.</p>
            </div>
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
              {forgotPasswordError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{forgotPasswordError}</div>}
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-[#6B7280] mb-1">Email Address</label>
                <input ref={forgotEmailInputRef} type="email" id="forgot-email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow" />
              </div>
              <button type="submit" disabled={isForgotPasswordLoading} className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300 disabled:opacity-50">
                {isForgotPasswordLoading ? <><SpinnerIcon /> Sending...</> : 'Send Reset Link'}
              </button>
            </form>
            <p className="text-center text-sm text-[#6B7280] mt-6">Remember your password?{' '}<button onClick={() => setModalView('login')} className="font-medium text-[#4F46E5] hover:text-[#6366F1]">Log in</button></p>
          </div>
        )}

        {modalView === 'resetEmailSent' && (
          <div>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-[#4F46E5]">Check Your Email</h1>
              <p className="text-[#6B7280] mt-4">We've sent a password reset link to <br/><strong>{forgotPasswordEmail}</strong>.</p>
              <p className="text-sm text-gray-500 mt-4">Please check your inbox (and spam folder) to continue.</p>
               <button onClick={closeModal} className="mt-6 w-full flex justify-center items-center gap-2 py-3 px-4 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300">
                Close
              </button>
            </div>
          </div>
        )}

        {modalView === 'signup' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-[#4F46E5]">Create an Account</h1>
              <p className="text-[#6B7280] mt-2">Join to get your unique brand name.</p>
            </div>
            <form onSubmit={handleSignupSubmit} className="space-y-4">
              {signupError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{signupError}</div>}
              <div>
                <label htmlFor="signup-name" className="block text-sm font-medium text-[#6B7280] mb-1">Full Name</label>
                <input ref={nameInputRef} type="text" id="signup-name" value={signupName} onChange={(e) => setSignupName(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow" />
              </div>
              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-[#6B7280] mb-1">Email Address</label>
                <input type="email" id="signup-email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow" />
              </div>
              <PasswordInput label="Password" id="signup-password" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} onStrengthChange={setIsSignupPasswordStrong} showStrengthMeter required minLength={8} />
              <PasswordInput label="Confirm Password" id="signup-confirm-password" value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} required />
              <button type="submit" disabled={isSignupLoading} className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300 disabled:opacity-50">{isSignupLoading ? <><SpinnerIcon /> Signing Up...</> : 'Sign Up'}</button>
            </form>
            <p className="text-center text-sm text-[#6B7280] mt-6">Already have an account? <button onClick={() => setModalView('login')} className="font-medium text-[#4F46E5] hover:text-[#6366F1]">Log in</button></p>
          </div>
        )}

      </div>
    </div>
  )
};
